import {
	BarChart3,
	Briefcase,
	Building2,
	CheckSquare,
	Contact,
	Globe,
	Headphones,
	Kanban,
	LayoutDashboard,
	type LucideIcon,
	MailOpen,
	Receipt,
	Settings,
	Settings2,
	Shield,
	TrendingUp,
	Users,
} from "lucide-react";

/**
 * Per-permission profile returned by admin.getAccessProfile — drives sidebar
 * gating and the root redirect (a sales-only user must never land on the
 * support-gated dashboard).
 */
export type AccessProfile = {
	canProvideSupport: boolean;
	canManagePricing: boolean;
	canManageSystem: boolean;
	canViewSales: boolean;
	canViewAnalytics: boolean;
	/** CRM flags (can_access/manage/admin_crm on the system). */
	canAccessCrm: boolean;
	canManageCrm: boolean;
	canAdminCrm: boolean;
};

export type NavBadge =
	| "openTasks"
	| "accessRequests"
	| "billingIssues"
	| "supportRequests";

export type NavItem = {
	to: string;
	icon: LucideIcon;
	labelKey: string;
	/** A live count shown next to the label. */
	badge?: NavBadge;
	/** More paths that highlight this item (the one settings entry). */
	matches?: string[];
};
/** `labelKey`: the group's heading (none = no heading, e.g. the dashboard). */
export type NavGroup = { key: string; labelKey?: string; items: NavItem[] };

// The dashboard's data (admin.analytics.* reads) is gated by
// can_view_analytics — platform-wide aggregates, granted to support AND sales.
const dashboardGroup: NavGroup = {
	key: "dashboard",
	items: [{ to: "/", icon: LayoutDashboard, labelKey: "admin:nav.dashboard" }],
};

/** Directories first, then the work queues with their counts. */
const supportGroup: NavGroup = {
	key: "support",
	labelKey: "admin:nav.group.support",
	items: [
		{ to: "/users", icon: Users, labelKey: "admin:nav.users" },
		{
			to: "/organizations",
			icon: Building2,
			labelKey: "admin:nav.organizations",
		},
		{
			to: "/access-requests",
			icon: MailOpen,
			labelKey: "admin:nav.access_requests",
			badge: "accessRequests",
		},
		{
			to: "/billing-issues",
			icon: Receipt,
			labelKey: "admin:nav.billing_issues",
			badge: "billingIssues",
		},
		{
			to: "/support",
			icon: Headphones,
			labelKey: "admin:nav.support",
			badge: "supportRequests",
		},
	],
};

/**
 * The "Sales" group (docs/plans/crm.md decision 13): the CRM + the existing
 * platform accounts view. Support (CRM read-only) sees companies + contacts.
 */
const salesItems = (profile: AccessProfile): NavItem[] => {
	const items: NavItem[] = [];
	if (profile.canManageCrm) {
		items.push({
			to: "/crm/opportunities",
			icon: Kanban,
			labelKey: "admin:nav.pipeline",
		});
	}
	if (profile.canAccessCrm) {
		items.push(
			{
				to: "/crm/organizations",
				icon: Briefcase,
				labelKey: "admin:nav.companies",
			},
			{ to: "/crm/contacts", icon: Contact, labelKey: "admin:nav.contacts" },
		);
	}
	if (profile.canManageCrm) {
		items.push({
			to: "/crm/tasks",
			icon: CheckSquare,
			labelKey: "admin:nav.my_tasks",
			badge: "openTasks",
		});
	}
	if (profile.canViewSales) {
		items.push({
			to: "/sales",
			icon: TrendingUp,
			labelKey: "admin:nav.accounts",
		});
	}
	if (profile.canManageCrm) {
		items.push({
			to: "/crm/reports",
			icon: BarChart3,
			labelKey: "admin:nav.crm_reports",
		});
	}
	return items;
};

/**
 * The settings pages this profile may open, in the order of the settings
 * page's tabs. The sidebar shows them as one "Asetukset" entry.
 */
export const settingsSections = (profile: AccessProfile): NavItem[] => [
	...(profile.canManageSystem
		? [
				{
					to: "/system-settings",
					icon: Settings,
					labelKey: "admin:nav.system_settings",
				},
			]
		: []),
	// Every CRM manager connects their own Google account here (crm-google-workspace G1).
	...(profile.canManageCrm
		? [
				{
					to: "/crm/settings",
					icon: Settings2,
					labelKey: "admin:nav.crm_settings",
				},
			]
		: []),
	...(profile.canManageSystem
		? [
				{
					to: "/translations",
					icon: Globe,
					labelKey: "admin:nav.translations",
				},
				{ to: "/user-roles", icon: Shield, labelKey: "admin:nav.user_roles" },
			]
		: []),
];

export const visibleNavGroups = (profile: AccessProfile): NavGroup[] => {
	const groups: NavGroup[] = [];
	if (profile.canViewAnalytics) groups.push(dashboardGroup);
	if (profile.canProvideSupport) groups.push(supportGroup);
	const sales = salesItems(profile);
	if (sales.length > 0) {
		groups.push({
			key: "sales",
			labelKey: "admin:nav.group.sales",
			items: sales,
		});
	}
	const settings = settingsSections(profile);
	const [first] = settings;
	if (first) {
		groups.push({
			key: "settings",
			items: [
				{
					to: first.to,
					icon: Settings,
					labelKey: "admin:nav.settings",
					matches: settings.map((item) => item.to),
				},
			],
		});
	}
	return groups;
};

/**
 * Where should `/` land for this profile? null = render the dashboard.
 */
export const resolveHomeRedirect = (profile: AccessProfile): string | null => {
	if (profile.canViewAnalytics) return null;
	if (profile.canViewSales) return "/sales";
	// A CRM-only role (crm_admin) has neither analytics nor /sales.
	if (profile.canAccessCrm) return "/crm";
	if (profile.canManageSystem) return "/system-settings";
	return null;
};
